import React, { useState, useEffect } from "react";
import { useDispatch, connect } from "react-redux";
import moment from "moment-timezone";
import {
  Row, Col, Card, CardBody,
} from "reactstrap";
import {
  Table, Thead, Tbody, Tr, Th, Td
} from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";

// i18n
import { withTranslation } from "react-i18next";
import CustomPagination from "components/Common/CustomPagination";
import TableLoader from "components/Common/TableLoader";
import { fetchUserLogs } from "store/actions";
import { LOG_LEVELS, LOG_TYPES } from "constants/logs";
import { MetaTags } from "react-meta-tags";
import { getNoteType } from "common/utils/getNoteType";
import { startCase } from "lodash";

const formatDate = (date, format = "DD/MM/YYYY, hh:mm A") => {
  let d = new Date(date);
  return `${moment(d).format(format)}`;
};

// NOTE: i18next's default nsSeparator (":") and keySeparator (".") both fire on
// props.t() calls in this app (see i18n.js - keySeparator:false is disabled),
// so a raw ":" anywhere in a translated string gets silently mangled. Never put
// a literal colon in a message built here - use parentheses instead.
const formatAuditValue = (value) => {
  if (value === undefined || value === null || value === "") return "none";
  if (Array.isArray(value)) {
    if (value.length === 0) return "none";
    if (typeof value[0] === "object" && value[0] !== null) {
      return value.map((item) => {
        if (item.currency) return item.groupPath ? `${item.currency} (${item.groupPath})` : item.currency;
        return item.title || Object.entries(item).map(([k, v]) => `${k}=${v}`).join(", ");
      }).join(", ");
    }
    return value.join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return Object.entries(value).map(([k, v]) => `${k}=${v}`).join(", ");
  return String(value);
};

const describeFieldChange = (field, oldValue, newValue) =>
  `${startCase(field)} (${formatAuditValue(oldValue)} → ${formatAuditValue(newValue)})`;

const IP_ROUTE_LABELS = {
  "cp-login": "Client Portal Login",
  "crm-login": "CRM Login",
  "cp-register": "Client Portal Registration",
  "cp-2fa": "Client Portal 2FA",
  "cp-login-as-client": "Login as Client",
};
const describeIpRoute = (route) => IP_ROUTE_LABELS[route] || route || "an endpoint";
const describeIp = (ip, country) => `${ip}${country ? ` - ${country}` : ""}`;
const AUTH_REASON_LABELS = {
  WRONG_PASSWORD: "wrong password",
  UNKNOWN_EMAIL: "unknown email",
  SUSPENDED: "account suspended",
  USER_INACTIVE: "user deactivated",
  ROLE_INACTIVE: "role deactivated",
  "2FA_INVALID": "wrong 2FA code",
  "2FA_REUSED": "2FA code reused",
  "2FA_NOT_CONFIGURED": "2FA not configured",
  USER_DEACTIVATED: "user deactivated",
  ACCESS_CHANGED: "role or email changed",
  USER_DELETED: "user deleted",
  PASSWORD_CHANGED: "password changed",
  PASSWORD_RESET: "password reset",
  "2FA_RESET_BY_ADMIN": "2FA reset by an admin",
};
const describeAuthReason = (reason) => AUTH_REASON_LABELS[reason] || (reason ? reason.toLowerCase().replace(/_/g, " ") : "");
const describeFrom = (details = {}) => (details.ip ? ` from ${describeIp(details.ip, details.ipCountry)}` : "");

function Activities(props) {
  const [sizePerPage, setSizePerPage] = useState(10);
  const generateLogs = (data) => {
    const {
      type,
      content = {},
      details = {},
      userId = {},
    } = data;
    const { error } = details;
    let message = "";
    if (error) {
      message = error;
      return message;
    }
    // destruct log content here
    //   const {
    // } = content;
    switch (type) {
      case LOG_TYPES.UPDATE_LEAD_STAGE:
        message = `${props.t("Lead stage updated from")} ${props.t(`${startCase(content.oldStage)}`)} ${props.t("to")} ${props.t(`${startCase(content.newStage)}`)}`;
        break;
      case LOG_TYPES.EDIT_CUSTOMER_INFO:
        message = props.t(`Customer (${data?.customerId?.email}) updated`);
        break;
      case LOG_TYPES.CREATE_USER:
        message = props.t(`New user (${content.email}) created`);
        break;
      case LOG_TYPES.UPDATE_USER:
        message = props.t(`User (${details.previousData.email}) updated`);
        break;
      case LOG_TYPES.DELETE_USER:
        message = props.t(`User (${content.deletedUserEmail}) deleted`);
        break;
      case LOG_TYPES.CREATE_TEAM:
        message = props.t(`New team (${content.title}) created`);
        break;
      case LOG_TYPES.UPDATE_TEAM:
        message = props.t(`Team (${details.newData.title}) updated`);
        break;
      case LOG_TYPES.ACCOUNT_TYPE_CREATED:
        message = props.t(`New account type (${content?.additionalDetails?.title}) created (${content?.additionalDetails?.type} / ${content?.additionalDetails?.platform})`);
        break;
      case LOG_TYPES.ACCOUNT_TYPE_UPDATED: {
        const changedFields = content?.additionalDetails?.changedFields || [];
        const fieldChanges = changedFields
          .map((field) => describeFieldChange(field, content?.oldValue?.[field], content?.newValue?.[field]))
          .join("; ");
        message = props.t(`Account type (${content?.additionalDetails?.title}) updated — ${fieldChanges}`);
        break;
      }
      case LOG_TYPES.ADD_TEAM_MEMBER:
        message = props.t(`New team member (${content?.member?.email}) added to ${content?.team}`);
        break;
      case LOG_TYPES.DELETE_TEAM_MEMBER:
        message = props.t(`Team member (${content.member}) deleted from ${content.team}`);
        break;
      case LOG_TYPES.ROLES:
        message = props.t(`New role (${content.title}) created`);
        break;
      case LOG_TYPES.UPDATE_ROLE:
        message = props.t(`Role (${details.newData.title}) updated`);
        break;
      case LOG_TYPES.DELETE_ROLE:
        message = props.t(`Role (${content.deletedRole}) deleted`);
        break;
      case LOG_TYPES.CREATE_LEAD:
        message = props.t(`Lead (${content.lead}) added`);
        break;
      case LOG_TYPES.ASSIGN_AGENT:
        message = props.t(`Assigned agent (${content.agent}) to client (${content.name})`);
        break;
      case LOG_TYPES.CHANGE_CALL_STATUS:
        message = props.t(`Call Status changed from (${content.oldCallStatus ?? "Nothing"}) to (${content.newCallStatus}) for client (${content.name})`);
        break;
      case LOG_TYPES.RESET_PASSWORD:
        message = `${props.t("The password has been reset for")} ${content?.customerName} `;
        break;
      case LOG_TYPES.CHANGE_PASSWORD:
        message = content?.changedBy && content?.targetUserId && content.changedBy !== content.targetUserId
          ? props.t(`Password changed for (${content?.customerName})`)
          : `${content?.customerName} ${props.t("has changed his password")}`;
        break;
      case LOG_TYPES.ENABLE_2FA:
        message = props.t(`User (${content.name}) activated two factor authentication.`);
        break;
      case LOG_TYPES.DISABLE_2FA:
        message = props.t(`User (${content.name}) disabled two factor authentication.`);
        break;
      case LOG_TYPES.DEPOSIT_CASHBACK:
        message = props.t(`${content?.customerId.firstName} ${content?.customerId.lastName} have received Deposit Cashback  of amount ${content.amount} to his USD wallet`);
        break;
      case LOG_TYPES.CREATE_MAM_PROFILE:
        message = props.t(`${content?.customer?.firstName} ${content?.customer?.lastName} have created MAM profile`);
        break;
      case LOG_TYPES.ADD_TODO:
      case LOG_TYPES.EDIT_TODO:
      case LOG_TYPES.DELETE_TODO:
        content.type = getNoteType(content.type);
        if (type === LOG_TYPES.ADD_TODO) {
          message = props.t(`Added new ${content.type} (${content.id}) for client ${content.customerName}`);
        } else if (type === LOG_TYPES.EDIT_TODO) {
          message = props.t(`Edited ${content.type} (${content.id}) for client ${content.customerName}`);
        } else if (type === LOG_TYPES.DELETE_TODO) {
          message = props.t(`Deleted ${content.type} (${content.id}) for client ${content.customerName}`);
        }
        break;
      case LOG_TYPES.ADD_CAMPAIGN_TEMPLATE:
        message = props.t(`New campaign template (${content.title}) created`);
        break;
      case LOG_TYPES.EDIT_CAMPAIGN_TEMPLATE:
        message = props.t(`Campaign template (${content.newData.title}) updated`);
        break;
      case LOG_TYPES.DELETE_CAMPAIGN_TEMPLATE:
        message = props.t(`Campaign tmeplate (${content.title}) deleted`);
        break;
      case LOG_TYPES.ADD_CAMPAIGN:
        message = props.t(`New campaign (${content.name}) scheduled on ` + formatDate(content.scheduleDate, "DD/MM/YYYY, hh;mm A"));
        break;
      case LOG_TYPES.EDIT_CAMPAIGN:
        message = props.t(`Campaign (${content.newData.name}) updated`);
        break;
      case LOG_TYPES.DELETE_CAMPAIGN:
        message = props.t(`Campaign (${content.name}) deleted`);
        break;
      case LOG_TYPES.RUN_CAMPAIGN:
        message = props.t(`Campaign (${content.name}) has run successully`);
        break;
      case LOG_TYPES.CREATE_MASTER_AGREEMENT:
        message = props.t(`Created a new master agreement (${content?.title})`);
        break;
      case LOG_TYPES.UPDATE_MASTER_AGREEMENT:
        message = props.t(`Updated master agreement (${content?.title})`);
        break;
      case LOG_TYPES.DELETE_AGREEMENT:
        message = props.t(`Deleted agreement (${content?.title})`);
        break;
      case LOG_TYPES.CREATE_SHARED_AGREEMENT:
        message = props.t(`Created a new shared agreement (${content?.title})`);
        break;
      case LOG_TYPES.UPDATE_SHARED_AGREEMENT:
        message = props.t(`Updated shared agreement (${content?.title})`);
        break;
      case LOG_TYPES.LINK_IB:
        message = props.t(`Linked IB (${content?.parentName}) to client (${content?.clientName})`);
        break;
      case LOG_TYPES.UNLINK_IB:
        message = props.t(`Unlinked IB (${content?.parentName}) from client (${content?.clientName})`);
        break;
      case LOG_TYPES.CREATE_CONVERSION:
        message = props.t(`Created conversion rate includes base currency ${content?.baseCurrency} and target currency ${content?.targetCurrency} and rate ${content.value}`);
        break;
      case LOG_TYPES.EDIT_CONVERSION:
        message = props.t("Updated conversion rate with ") + content
          ?.map((item) =>
            Object.entries(item)
              .map(([key, value]) => {
                let renamedKey = "";
                if (key === "value") {
                  renamedKey = "rate";
                }
                else if (key === "targetCurrency") {
                  renamedKey = "target currency";
                }
                else {
                  renamedKey = "base currency";
                }
                return `${renamedKey} from ${value.current} to ${value.new}`;
              })
              .join(", ")
          )
          .join(", and ");
        break;
      case LOG_TYPES.DEPOSIT:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved wallet deposit with amount ${content.amount?.$numberDecimal || content.amount}`);
        break;
      case LOG_TYPES.FX_DEPOSIT:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved forex deposit with amount ${content.amount?.$numberDecimal || content.amount} for account ${content.login}`);
        break;
      case LOG_TYPES.FX_WITHDRAW:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved forex withdraw with amount ${content.amount?.$numberDecimal || content.amount} for account ${content.login}`);
        break;
      case LOG_TYPES.WITHDRAW:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved wallet withdraw with amount ${content.amount?.$numberDecimal || content.amount}`);
        break;
      case LOG_TYPES.DEPOSIT_FAILED:
        message = props.t(`Failed deposit transaction for Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.APPROVING_DEPOSIT:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved approving deposit for amount ${content?.amount?.$numberDecimal || content?.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.REJECTION_DEPOSIT:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved rejection deposit with for amount ${content?.amount?.$numberDecimal || content?.amount} with recordId ${content.recordId} `);
        break;
      case LOG_TYPES.APPROVING_WITHDRAW:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved approving withdraw for amount ${content?.amount?.$numberDecimal || content?.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.REJECTION_WITHDRAW:
        message = props.t(`Customer (${content?.customerId?.firstName} ${content?.customerId?.lastName}) recieved rejection withdraw for amount ${content?.amount?.$numberDecimal || content?.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.INTERNAL_TRANSFER:
        message = props.t(` ${!content?.isClientToClient ? `Internally tranfer ${content.amount} for ${content?.from?.client}` : `Tranfer ${content.amount} from ${content?.from?.client} to ${content?.to?.client}`} - ( ${content?.from?.source} ${content?.from?.isWallet ? "Wallet" : "Acccount"}  => ${content?.to?.source} ${content?.to?.isWallet ? "Wallet" : "Acccount"} ) `);
        break;
      case LOG_TYPES.FX_INTERNAL_TRANSFER:
        message = props.t(`Tranfer amount ${content.amount} from account ${content?.from} to account ${content.to}`);
        break;
      case LOG_TYPES.APPROVING_DEPOSIT_FX:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved approving FX deposit for amount ${content.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.REJECTION_DEPOSIT_FX:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved rejection FX deposit for amount ${content.amount}  with  recordId ${content.recordId}`);
        break;
      case LOG_TYPES.APPROVING_WITHDRAW_FX:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved approving FX withdraw for amount ${content.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.REJECTION_WITHDRAW_FX:
        message = props.t(`Customer (${content?.customer?.firstName} ${content?.customer?.lastName}) recieved rejection FX withdraw with for amount ${content.amount} with recordId ${content.recordId}`);
        break;
      case LOG_TYPES.FX_INTERNAL_TRANSFER_UPDATE:
        message = props.t(`${content.status === "APPROVED" ? "Approving" : "Rejection"} transfer amount (${content?.amount} from account ${content.from} to account ${content.to}`);
        break;
      case LOG_TYPES.INTERNAL_TRANSFER_UPDATE:
        message = props.t(`${content.status === "APPROVED" ? "Approving" : "Rejection"} transfer amount ${content?.amount} from account ${content.from} to account ${content.to}`);
        break;
      case LOG_TYPES.APPROVING_INTERNAL_TRANSFER:
        message = props.t(`Approving transfer for ${content.client}  to transaction have recordId ${content.recordId}`);
        break;
      case LOG_TYPES.REJECTION_INTERNAL_TRANSFER:
        message = props.t(`Rejection transfer for ${content.client}  to transaction have recordId ${content.recordId}`);
        break;
      case LOG_TYPES.TRADING_ACCOUNT_LEVERAGE_CHANGED:
        message = props.t(`Leverage Changed For Login (${content?.additionalDetails?.login}) From (${content?.oldValue?.leverage}) to (${content?.newValue?.leverage})`);
        break;
      case LOG_TYPES.TRADING_ACCOUNT_TYPE_CHANGED:
        message = props.t(`Trading account (Login ${content?.additionalDetails?.login}) type changed from (${content?.oldValue?.title || "N/A"}) to (${content?.newValue?.title || "N/A"})`);
        break;
      case LOG_TYPES.TRADING_ACCOUNT_ACCESS_CHANGED:
        message = props.t(`Trading account (Login ${content?.additionalDetails?.login}) ${content?.newValue?.isActive ? "became active" : "became inactive"}`);
        break;
      case LOG_TYPES.TRADING_ACCOUNT_UPDATED: {
        const changedFields = content?.additionalDetails?.changedFields || [];
        const fieldChanges = changedFields
          .map((field) => describeFieldChange(field, content?.oldValue?.[field], content?.newValue?.[field]))
          .join("; ");
        message = props.t(`Trading account (Login ${content?.additionalDetails?.login}) updated — ${fieldChanges}`);
        break;
      }
      case LOG_TYPES.TRADING_ACCOUNT_PLATFORM_UPDATED: {
        const newValue = content?.newValue || {};
        const fieldChanges = Object.keys(newValue)
          .map((field) => describeFieldChange(field, content?.oldValue?.[field], newValue[field]))
          .join("; ");
        message = props.t(`Trading account (Login ${content?.additionalDetails?.login}) platform settings updated — ${fieldChanges}`);
        break;
      }
      case LOG_TYPES.ACCOUNT_CREATED:
        message = props.t(`Created a new trading account ${content?.newValue?.login ?? content?.login} (${content?.newValue?.platform ?? content?.platform})`);
        break;
      case LOG_TYPES.IB_ACCOUNT_CREATED: {
        const nv = content?.newValue || {};
        message = nv.login
          ? props.t(`Created IB trading account ${nv.login} (${nv.platform || "N/A"})`)
          : props.t(`Created IB wallet (${nv.currency || "N/A"})`);
        break;
      }
      case LOG_TYPES.SYNC_DEALS_STARTED: {
        const triggeredByName = `${userId?.firstName || ""} ${userId?.lastName || ""}`.trim() || "System";
        message = props.t(`Sync Deals triggered by (${triggeredByName})`);
        break;
      }
      case LOG_TYPES.SYNC_DEALS_COMPLETED:
        message = props.t(`(${content?.additionalDetails?.totalDeals ?? 0}) Deals Synced`);
        break;
      case LOG_TYPES.WALLET:
        message = props.t(`Wallet created successfully (${content?.symbol || content?.asset || "N/A"})`);
        break;
      case LOG_TYPES.USER_EMAIL:
        message = props.t(`Email sent to ${content?.to || "N/A"}${content?.subject ? ` — subject: ${content.subject}` : ""}`);
        break;
      case LOG_TYPES.IP_ATTEMPT_FAILED:
        message = props.t(`Failed attempt from IP (${describeIp(content?.ip, content?.country)}) on ${describeIpRoute(content?.route)}`);
        break;
      case LOG_TYPES.IP_BLOCKED_ATTEMPT:
        message = props.t(`Blocked IP (${describeIp(content?.ip, content?.country)}) attempted ${describeIpRoute(content?.route)} again`);
        break;
      case LOG_TYPES.IP_AUTO_BLOCKED:
        message = props.t(`IP (${describeIp(content?.ip, content?.country)}) auto-blocked after ${content?.failCount} failed attempts (expires ${content?.expiresAt ? formatDate(content.expiresAt) : "N/A"})`);
        break;
      case LOG_TYPES.IP_BLOCK_CHECK_ERROR:
        message = props.t(`IP block-check failed for (${content?.ip}) — request was allowed through`);
        break;
      case LOG_TYPES.IP_BLOCK_ADDED:
        message = props.t(`IP (${describeIp(content?.ip, content?.country)}) manually blocked${content?.note ? ` (${content.note})` : ""}${content?.expiresAt ? ` until ${formatDate(content.expiresAt)}` : " permanently"}`);
        break;
      case LOG_TYPES.IP_BLOCK_REMOVED:
        message = props.t(`IP (${content?.ip}) unblocked`);
        break;
      case LOG_TYPES.IP_SECURITY_CONFIG_UPDATED: {
        const patchSummary = Object.entries(content?.patch || {}).map(([k, v]) => `${startCase(k)} ${v}`).join(", ");
        message = props.t(`IP security settings updated (${patchSummary || "no changes"})`);
        break;
      }
      case LOG_TYPES.CRM_LOGIN:
        message = props.t(`Signed in${describeFrom(details)}${details?.reason === "PASSWORD_2FA" || details?.reason === "PASSWORD_2FA_SETUP" ? " with 2FA" : ""}`);
        break;
      case LOG_TYPES.LOGIN_FAILED:
        message = props.t(`Failed sign-in${details?.email ? ` for ${details.email}` : ""}${describeFrom(details)} (${describeAuthReason(details?.reason)})`);
        break;
      case LOG_TYPES.LOGIN_2FA_FAILED:
        message = props.t(`Failed 2FA code${describeFrom(details)} (${describeAuthReason(details?.reason)})`);
        break;
      case LOG_TYPES.ACCOUNT_LOCKED:
        message = props.t(`Account${details?.email ? ` ${details.email}` : ""} locked after repeated failed attempts${describeFrom(details)}`);
        break;
      case LOG_TYPES.LOGOUT:
        message = props.t(`Signed out${describeFrom(details)}`);
        break;
      case LOG_TYPES.SESSIONS_REVOKED:
        message = props.t(`All sessions ended (${describeAuthReason(content?.reason || details?.reason)})`);
        break;
      default:
        message = content?.body || details?.message || content?.action || "";
        break;
    }

    return message;
  };

  const getTriggeredBy = (val) => {
    let name = "";
    if (val.userLog && val.userId) {
      const {
        firstName,
        lastName,
      } = val.userId;
      name = `${firstName} ${lastName}`;
    } else {
      name = "System";
    }
    return name;
  };

  const columns = [
    {
      dataField: "recordId",
      text: props.t("ID")
    },
    {
      dataField: "triggeredBy",
      text: props.t("Triggered By"),
      formatter: (val) => getTriggeredBy(val),
    },
    {
      dataField: "level",
      text: props.t("Log Level"),
      formatter: (val) => (LOG_LEVELS[val.level] ? LOG_LEVELS[val.level] : "Err"),
    },
    {
      dataField: "type",
      text: props.t("Log Type"),
      formatter: (val) => (LOG_TYPES[val.type] ? startCase(val.type.toLowerCase()) : "Err"),
    },
    {
      dataField: "createdAt",
      text: props.t("Date Created"),
      formatter: (val) => formatDate(val.createdAt),
    },
    {
      dataField: "content",
      text: props.t("Message"),
      formatter: (val) => (generateLogs(val))
    },
  ];

  const dispatch = useDispatch();
  const fetchData = async (page) => {
    dispatch(fetchUserLogs({
      page: page || 1,
      limit: sizePerPage,
      userLog: true
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [sizePerPage, 1]);
  return (
    <React.Fragment>
      <MetaTags>
        <title>
          {props.t("User Logs")}
        </title>
      </MetaTags>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col className="col-12">
              <h2>{props.t("User Logs")}</h2>
              <Card>
                <CardBody>
                  <div className="table-rep-plugin">
                    <div
                      className="table-responsive mb-0"
                      data-pattern="priority-columns"
                    >
                      <Table
                        id="tech-companies-1"
                        className="table  table-hover "
                      >
                        <Thead className="text-center table-light" >
                          <Tr>
                            {columns.map((column, index) =>
                              <Th data-priority={index} key={index}><span className="color-primary">{column.text}</span></Th>
                            )}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {props.loading && <TableLoader colSpan={4} />}
                          {!props.loading && props.todos.length === 0 &&
                            <>
                              <Tr>
                                <Td colSpan={"100%"} className="fw-bolder text-center" st="true">
                                  <h3 className="fw-bolder text-center">No records</h3>
                                </Td>
                              </Tr>
                            </>
                          }
                          {!props.loading && props.todos.map((row, rowIndex) =>
                            <Tr key={rowIndex}>
                              {columns.map((column, index) =>
                                <Td key={`${rowIndex}-${index}`} className="text-center">
                                  {column.formatter ? column.formatter(row, rowIndex) : row[column.dataField]}
                                </Td>
                              )}
                            </Tr>
                          )}
                        </Tbody>

                      </Table>
                      <CustomPagination
                        {...props.pagination}
                        docs={props.todos}
                        setSizePerPage={setSizePerPage}
                        sizePerPage={sizePerPage}
                        onChange={fetchData}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  clientDetails: state.clientReducer.clientDetails || {},
  todos: state.logsReducer.logs && state.logsReducer.logs.docs || [],
  pagination: state.logsReducer.logs || {},
  loading: state.logsReducer.loading,
  selectedClient: state.clientReducer.clientDetails || {},
});

export default connect(mapStateToProps, null)(withTranslation()(Activities));
